import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
	Box,
	Button,
	IconButton,
	Paper,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import React, { ErrorInfo, ReactNode } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router-dom";
import { formatDateTimeWithSeconds } from "../utils/date-formatting";

type RootErrorBoundaryProps = {
	children: ReactNode;
};

type RootErrorBoundaryState = {
	hasError: boolean;
	latestError: Error | null;
	latestStack: string | null;
	errorTimestamp: string | null;
	isChunkLoadError: boolean;
};

export class RootErrorBoundary extends React.Component<
	RootErrorBoundaryProps,
	RootErrorBoundaryState
> {
	state: RootErrorBoundaryState = {
		hasError: false,
		latestError: null,
		latestStack: null,
		errorTimestamp: null,
		isChunkLoadError: false,
	};

	private static isChunkLoadError(error: Error): boolean {
		const message = error.message || "";
		return (
			message.includes("Failed to fetch dynamically imported module") ||
			message.includes("Loading chunk") ||
			message.includes("ChunkLoadError")
		);
	}

	static getDerivedStateFromError(error: Error): RootErrorBoundaryState {
		return {
			hasError: true,
			latestError: error,
			latestStack: null,
			errorTimestamp: new Date().toISOString(),
			isChunkLoadError: RootErrorBoundary.isChunkLoadError(error),
		};
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		console.error("RootErrorBoundary caught an error", error, info);
		this.setState({ latestStack: info.componentStack });

		// For chunk load errors (after deployment), auto-reload after brief delay
		if (RootErrorBoundary.isChunkLoadError(error)) {
			setTimeout(() => {
				window.location.reload();
			}, 2000);
			return; // Don't report chunk errors to Sentry - they're not bugs
		}

		// Lazy-load Sentry and capture exception
		void import("@sentry/react")
			.then((Sentry) => {
				if (Sentry.isInitialized()) {
					Sentry.captureException(error, {
						extra: { componentStack: info.componentStack },
					});
				}
			})
			.catch((err) => {
				console.error("Failed to load Sentry for error reporting", err);
			});
	}

	private readonly handleReload = (): void => {
		window.location.reload();
	};

	private readonly handleCopyDetails = async (): Promise<void> => {
		const details = this.buildErrorDetails();
		try {
			await navigator.clipboard.writeText(details);
		} catch (error) {
			console.error("Failed to copy error details", error);
		}
	};

	private buildErrorDetails(): string {
		const url = window.location.href;
		const timestamp = this.state.errorTimestamp
			? formatDateTimeWithSeconds(this.state.errorTimestamp)
			: "unknown";
		const message = this.state.latestError?.message ?? "No message";
		const stack = this.state.latestStack ?? "No stack";
		return [
			`Timestamp: ${timestamp}`,
			`URL: ${url}`,
			`Message: ${message}`,
			"Component Stack:",
			stack,
		].join("\n");
	}

	render() {
		if (this.state.hasError) {
			// Show simple auto-reload message for chunk load errors
			if (this.state.isChunkLoadError) {
				return (
					<Box
						sx={{
							p: 3,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							minHeight: "50vh",
						}}
						data-component="chunk-error-boundary"
					>
						<Paper
							sx={{ p: 4, textAlign: "center" }}
							data-component="chunk-error-card"
						>
							<Typography variant="h5" sx={{ mb: 2 }}>
								New version available
							</Typography>
							<Typography
								variant="body1"
								color="text.secondary"
								sx={{ mb: 2 }}
							>
								The application has been updated. Reloading
								automatically...
							</Typography>
							<Button
								variant="contained"
								onClick={this.handleReload}
								data-component="reload-now-button"
							>
								Reload now
							</Button>
						</Paper>
					</Box>
				);
			}

			const url = window.location.href;
			const timestamp = this.state.errorTimestamp
				? formatDateTimeWithSeconds(this.state.errorTimestamp)
				: "unknown";
			return (
				<Box
					sx={{ p: 3, display: "flex", justifyContent: "center" }}
					data-component="root-error-boundary"
				>
					<Paper sx={{ p: 3, maxWidth: 640 }} data-component="error-card">
						<Stack
							direction="row"
							justifyContent="space-between"
							alignItems="center"
							sx={{ mb: 2 }}
						>
							<Typography variant="h5">Something went wrong</Typography>
							<Tooltip title="Copy error details">
								<IconButton
									size="small"
									onClick={this.handleCopyDetails}
									data-component="error-copy-button"
								>
									<ContentCopyIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</Stack>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ mb: 2 }}
						>
							Please reload the page. If the issue persists, contact
							support with the error details below.
						</Typography>
						<Paper
							sx={{
								p: 2,
								mb: 2,
								backgroundColor: "action.hover",
							}}
							data-component="error-details"
						>
							<Stack spacing={1}>
								<Typography variant="subtitle2">Timestamp</Typography>
								<Typography variant="caption">{timestamp}</Typography>
								<Typography variant="subtitle2">URL</Typography>
								<Typography
									variant="caption"
									sx={{ wordBreak: "break-all" }}
								>
									{url}
								</Typography>
								{this.state.latestError && (
									<>
										<Typography variant="subtitle2">
											Error message
										</Typography>
										<Typography
											variant="caption"
											sx={{
												display: "block",
												whiteSpace: "pre-wrap",
											}}
											color="error"
										>
											{this.state.latestError.message}
										</Typography>
									</>
								)}
								{this.state.latestStack && (
									<>
										<Typography variant="subtitle2">
											Component stack
										</Typography>
										<Typography
											variant="caption"
											sx={{
												display: "block",
												whiteSpace: "pre-wrap",
											}}
											color="text.secondary"
										>
											{this.state.latestStack}
										</Typography>
									</>
								)}
							</Stack>
						</Paper>
						<Button
							variant="contained"
							onClick={this.handleReload}
							data-component="reload-button"
						>
							Reload page
						</Button>
					</Paper>
				</Box>
			);
		}
		return this.props.children;
	}
}

function reportErrorToSentry(error: Error, componentStack?: string): void {
	void import("@sentry/react")
		.then((Sentry) => {
			if (Sentry.isInitialized()) {
				Sentry.captureException(error, {
					extra: { componentStack },
				});
			}
		})
		.catch((err) => {
			console.error("Failed to load Sentry for error reporting", err);
		});
}

function extractError(routeError: unknown): Error {
	if (routeError instanceof Error) return routeError;
	if (isRouteErrorResponse(routeError)) {
		return new Error(
			`${routeError.status} ${routeError.statusText}: ${routeError.data}`
		);
	}
	return new Error(String(routeError));
}

export function RouteErrorBoundary() {
	const routeError = useRouteError();
	const error = extractError(routeError);
	const timestamp = formatDateTimeWithSeconds(new Date().toISOString());
	const url = window.location.href;

	console.error("RouteErrorBoundary caught an error", error);
	reportErrorToSentry(error);

	return (
		<Box
			sx={{ p: 3, display: "flex", justifyContent: "center" }}
			data-component="root-error-boundary"
		>
			<Paper sx={{ p: 3, maxWidth: 640 }} data-component="error-card">
				<Stack
					direction="row"
					justifyContent="space-between"
					alignItems="center"
					sx={{ mb: 2 }}
				>
					<Typography variant="h5">Something went wrong</Typography>
				</Stack>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Please reload the page. If the issue persists, contact support with
					the error details below.
				</Typography>
				<Paper
					sx={{
						p: 2,
						mb: 2,
						backgroundColor: "action.hover",
					}}
					data-component="error-details"
				>
					<Stack spacing={1}>
						<Typography variant="subtitle2">Timestamp</Typography>
						<Typography variant="caption">{timestamp}</Typography>
						<Typography variant="subtitle2">URL</Typography>
						<Typography variant="caption" sx={{ wordBreak: "break-all" }}>
							{url}
						</Typography>
						<Typography variant="subtitle2">Error message</Typography>
						<Typography
							variant="caption"
							sx={{
								display: "block",
								whiteSpace: "pre-wrap",
							}}
							color="error"
						>
							{error.message}
						</Typography>
					</Stack>
				</Paper>
				<Button
					variant="contained"
					onClick={() => window.location.reload()}
					data-component="reload-button"
				>
					Reload page
				</Button>
			</Paper>
		</Box>
	);
}
